import { useMemo, useState } from "react";
import { useGeolocation } from "./hooks/useGeolocation";
import { useLiveVehicles } from "./hooks/useBusData";
import { usePlymouthStops } from "./hooks/usePlymouthStops";
import { getNearbyStops } from "./utils/bods";
import BusMap from "./components/BusMap";
import DepartureBoard from "./components/DepartureBoard";
import LocalAreaBoard from "./components/LocalAreaBoard";
import RoutesList from "./components/RoutesList";
import "./App.css";

const TABS = [
  { id: "nearby", label: "Stops", icon: "🚏" },
  { id: "board", label: "Board", icon: "🕐" },
  { id: "routes", label: "Routes", icon: "🗺" },
];

const DEFAULT_LIST_LIMIT = 60;

export default function App() {
  const [tab, setTab] = useState("nearby");
  const [selectedStop, setSelectedStop] = useState(null);
  const [search, setSearch] = useState("");
  const { location, loading: locLoading, error: locError, request: requestLocation } = useGeolocation();
  const { vehicles, error: vehicleError, lastUpdated } = useLiveVehicles();
  const { stops: plymouthStops, loading: stopsLoading, error: stopsError, source, reload } = usePlymouthStops();

  const sortedStops = useMemo(() => {
    const baseStops = location
      ? getNearbyStops(location.lat, location.lng, 50, plymouthStops)
      : [...plymouthStops].sort((a, b) => a.name.localeCompare(b.name));

    const q = search.trim().toLowerCase();
    if (!q) return baseStops;

    return baseStops.filter((stop) => {
      return [stop.name, stop.locality, stop.street, stop.id]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q));
    });
  }, [location, plymouthStops, search]);

  const listStops = sortedStops.slice(0, DEFAULT_LIST_LIMIT);

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
    setTab("nearby");
  };

  const handleTabChange = (t) => {
    setTab(t);
    if (t !== "nearby") setSelectedStop(null);
  };

  return (
    <div className={`app ${selectedStop ? "app--wide-board" : ""}`}>
      <header className="header">
        <div className="header-icon">🚌</div>
        <div>
          <h1>Plymouth Bus</h1>
          <p>
            {lastUpdated
              ? `Live vehicles · Updated ${lastUpdated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
              : vehicleError
              ? "Stops loaded · live vehicles unavailable"
              : "Connecting to live data…"}
          </p>
        </div>
      </header>

      <BusMap
        stops={plymouthStops}
        vehicles={vehicles}
        userLocation={location}
        selectedStop={selectedStop}
        onStopClick={handleStopClick}
      />

      <nav className="tab-bar" aria-label="App sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`tab ${tab === t.id ? "active" : ""}`}
            onClick={() => handleTabChange(t.id)}
            aria-current={tab === t.id ? "page" : undefined}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </nav>

      <main>
        {tab === "nearby" && !selectedStop && (
          <div>
            {!location && !locLoading && (
              <button className="loc-btn" onClick={requestLocation}>
                📍 Use my location to sort nearest stops first
              </button>
            )}

            {locLoading && <div className="info-box">Finding your location…</div>}

            {locError && (
              <div className="info-box info-box--amber">
                Could not get location — showing all Plymouth stops alphabetically.
              </div>
            )}

            {stopsError && (
              <div className="info-box info-box--amber">
                <strong>Full Plymouth stop list unavailable.</strong><br />
                Showing local fallback stops for now. {stopsError}
                <br />
                <button className="inline-btn" onClick={reload}>Try loading again</button>
              </div>
            )}

            <div className="section-head">
              <div>
                <div className="section-label">All Plymouth bus stops</div>
                <div className="section-subtitle">
                  {stopsLoading
                    ? "Loading Plymouth stops…"
                    : `${plymouthStops.length} stops · ${source}${location ? " · nearest first" : ""}`}
                </div>
              </div>
            </div>

            <input
              className="stop-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by stop, street, locality or ATCO code"
              aria-label="Search Plymouth bus stops"
            />

            <div className="card">
              {listStops.length === 0 && (
                <div className="empty-state">No Plymouth stops matched your search.</div>
              )}

              {listStops.map((stop) => (
                <button
                  key={stop.id}
                  className="stop-row"
                  onClick={() => setSelectedStop(stop)}
                >
                  <div className="stop-icon">🚏</div>
                  <div className="stop-details">
                    <div className="stop-name">{stop.name}</div>
                    <div className="stop-meta">
                      {stop.distanceKm !== undefined
                        ? `${stop.distanceKm < 1 ? `${Math.round(stop.distanceKm * 1000)}m` : `${stop.distanceKm.toFixed(1)}km`} away · `
                        : ""}
                      {[stop.street, stop.locality, stop.id].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <span className="chevron">›</span>
                </button>
              ))}
            </div>

            {!stopsLoading && sortedStops.length > DEFAULT_LIST_LIMIT && (
              <div className="list-note">
                Showing first {DEFAULT_LIST_LIMIT} of {sortedStops.length} stops. Use search to narrow the list.
              </div>
            )}
          </div>
        )}

        {tab === "nearby" && selectedStop && (
          <LocalAreaBoard
            anchorStop={selectedStop}
            stops={plymouthStops}
            vehicles={vehicles}
            onBack={() => setSelectedStop(null)}
          />
        )}

        {tab === "board" && (
          <DepartureBoard
            stop={selectedStop || plymouthStops[0]}
            vehicles={vehicles}
            onBack={() => setTab("nearby")}
          />
        )}

        {tab === "routes" && <RoutesList />}
      </main>
    </div>
  );
}
